import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import DeleteIcon from '@mui/icons-material/Delete'
import IconButton from '@mui/material/IconButton'
import LoadingButton from '@mui/lab/LoadingButton'
import cmsApi from '../../api/cms'
import upload from '../../api/upload'
import BarraPagineCms from '../../components/cms/BarraPagineCms'
import GestisciComponenti from '../../components/cms/GestisciComponenti'
import HeaderCms from '../../components/cms/HeaderCms'
import ModalAnnullaCms from '../../components/cms/ModalAnnullaCms'
import RicercaCms from '../../components/cms/RicercaCms'
import BottoniScheda from '../../components/scheda/BottoniScheda'
import MetaScheda from '../../components/scheda/MetaScheda'
import FAQScheda from '../../components/scheda/FAQScheda'
import config from '../../config'
import useAuth from '../../controllers/auth/useAuth'
import useCms from '../../controllers/cms/useCms'
import { createFaqListHandlers, normalizeFaqList } from '../../utils/faqUtils'
import { buildCmsPaginaFaqPayload } from '../../utils/faqPersist'

const LANG_KEYS = ['italiano', 'inglese', 'tedesco', 'olandese', 'danese', 'polacco']

function getFirstPageImage(sezioni) {
  for (let i = 0; i < (sezioni || []).length; i++) {
    const comps = sezioni[i].componenti || []
    const img = comps.find((c) => c.tipo === 3)
    if (img?.link_immagine) return img.link_immagine
  }
  return null
}

const MAX_CMS_FAQ_BODY_CHARS = 16000

function htmlToPlainTextForFaq(html) {
  if (!html || typeof html !== 'string') return ''
  return html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function buildCmsFaqPageTitle(pagina, langKey) {
  if (!pagina) return ''
  const meta = pagina[`meta_title_${langKey}`]
  if (meta != null && String(meta).trim()) return String(meta).trim()
  return pagina.nome != null && String(pagina.nome).trim() ? String(pagina.nome).trim() : ''
}

function buildCmsFaqPageBody(sezioni, langKey, pagina) {
  const titoloKey = `titolo_${langKey}`
  const valoreKey = `valore_${langKey}`
  const linkKey = `link_${langKey}`
  const parts = []
  if (pagina) {
    const md = pagina[`meta_description_${langKey}`]
    if (md != null && String(md).trim()) parts.push(`Summary (meta): ${String(md).trim()}`)
  }
  for (const sezione of sezioni || []) {
    const label = sezione?.nome != null && String(sezione.nome).trim() !== '' ? String(sezione.nome).trim() : null
    if (label) parts.push(`Section: ${label}`)
    for (const comp of sezione?.componenti || []) {
      const tipo = comp?.tipo
      if (tipo === 1) {
        const t = htmlToPlainTextForFaq(comp[valoreKey])
        if (t) parts.push(t)
      } else if (tipo === 2) {
        const t = comp[titoloKey] != null && String(comp[titoloKey]).trim() ? String(comp[titoloKey]).trim() : ''
        if (t) parts.push(t)
      } else if (tipo === 4) {
        const t = comp[linkKey] != null && String(comp[linkKey]).trim() ? String(comp[linkKey]).trim() : ''
        if (t) parts.push(t)
      }
    }
  }
  const combined = parts.join('\n\n')
  if (combined.length <= MAX_CMS_FAQ_BODY_CHARS) return combined
  return `${combined.slice(0, MAX_CMS_FAQ_BODY_CHARS)}\n…`
}

function HomeCms(props) {
  const { state, pathname } = useLocation()
  const navigate = useNavigate()
  const { pagine, elementi, modificaElemento, modificaPagina } = useCms()
  const { utente } = useAuth()

  const [pagina, setPagina] = useState()
  const [sezioni, setSezioni] = useState([])
  const [sezioneSel, setSezioneSel] = useState()
  const [lingua, setLingua] = useState(1)
  const [modifiche, setModifiche] = useState({ tipo: 0, appoPagina: null })
  const [altro, setAltro] = useState({
    loading: false,
    errore: null,
    openAlert: 0,
    modifiche: false,
    visible: false,
  })
  const [confirmDeleteOgImage, setConfirmDeleteOgImage] = useState({ open: false, loading: false })
  const [faqList, setFaqList] = useState([])
  const prevPaginaIdRef = useRef(undefined)

  const setFaqAbilitatoPagina = React.useCallback((checked) => {
    setPagina((prev) => (prev ? { ...prev, faq_abilitato: checked } : prev))
  }, [])

  const { cambiaFaqAbilitato, cambiaFaqItem, aggiungiFaqItem, rimuoviFaqItem, trimFaqListToLength } = useMemo(
    () =>
      createFaqListHandlers({
        setFaqList,
        setFaqAbilitato: setFaqAbilitatoPagina,
        onDirty: () => setAltro((prev) => ({ ...prev, modifiche: true })),
      }),
    [setFaqAbilitatoPagina]
  )

  useEffect(() => aggStato(), [state, pagine])
  useEffect(() => aggSezioni(), [pagina, elementi])
  useEffect(() => {
    if (!pagina) {
      prevPaginaIdRef.current = undefined
      return
    }
    if (prevPaginaIdRef.current !== pagina.id) {
      prevPaginaIdRef.current = pagina.id
      setFaqList(normalizeFaqList(pagina.faq))
    }
  }, [pagina])

  const aggStato = () => {
    if (!state) return
    if (state.paginaCreata) {
      setAltro({ ...altro, openAlert: 1 })
      setPagina(state.paginaCreata)
      return navigate(pathname)
    }
    if (state.paginaModificata) {
      setAltro({ ...altro, openAlert: 2 })
      setPagina(state.paginaModificata)
      return navigate(pathname)
    }
    if (state.sezioneCreata) {
      setAltro({ ...altro, openAlert: 3 })
      const idPagina = state.sezioneCreata.pagina
      const appo = pagine.filter((p) => p.id == idPagina)[0]
      setPagina(appo)
      setSezioneSel(state.sezioneCreata)
      return navigate(pathname)
    }
    if (state.sezioneModificata) {
      setAltro({ ...altro, openAlert: 4 })
      const idPagina = state.sezioneModificata.pagina
      const appo = pagine.filter((p) => p.id == idPagina)[0]
      setPagina(appo)
      setSezioneSel(state.sezioneModificata)
      return navigate(pathname)
    }
    if (state.annullaSezione) {
      let appo
      if (state.idSezione) {
        appo = JSON.parse(JSON.stringify(elementi)).filter((el) => el.id == state.idSezione)[0]
        if (appo) {
          setSezioneSel(appo)
          const idPagina = appo.pagina
          appo = pagine.filter((p) => p.id == idPagina)[0]
          setPagina(appo)
        }
      }
      if (state.idPagina) {
        appo = pagine.filter((p) => p.id == state.idPagina)[0]
        setPagina(appo)
      }
    }
    if (state.paginaEliminata) {
      setAltro({ ...altro, openAlert: 6 })
      return navigate(pathname)
    }
    if (state.sezioneEliminata) {
      setAltro({ ...altro, openAlert: 7 })
      return navigate(pathname)
    }
  }

  const aggSezioni = () => {
    if (!pagina) setSezioneSel(null)
    if (!pagina) return setSezioni([])
    const vett = JSON.parse(JSON.stringify(elementi))
    setSezioni(vett.filter((el) => el.pagina == pagina.id))
  }

  const scegliPagina = (pagina) => {
    if (altro.modifiche) {
      setModifiche({ tipo: 1, appoPagina: pagina })
      return setAltro({ ...altro, visible: true })
    }
    setPagina(pagina)
    setSezioneSel(null)
  }

  const scegliSezione = (id) => {
    if (id === 'seo') {
      setSezioneSel({ id: 'seo', nome: 'SEO' })
      return
    }
    if (id === 'faq') {
      setSezioneSel({ id: 'faq', nome: 'FAQ' })
      return
    }
    const appo = sezioni.filter((s) => s.id == id)[0]
    if (appo) setSezioneSel(appo)
    else setSezioneSel(null)
  }

  const annullaSezione = () => {
    if (altro.modifiche) {
      setModifiche({ tipo: 2, appoPagina: null })
      return setAltro({ ...altro, visible: true })
    }
    setSezioneSel(null)
  }

  const cambiaLink = (pos, lang, valore) => {
    if (!sezioneSel) return
    const appo = { ...sezioneSel }
    if (lang == 1) appo.componenti[pos].link_italiano = valore
    if (lang == 2) appo.componenti[pos].link_inglese = valore
    if (lang == 3) appo.componenti[pos].link_tedesco = valore
    if (lang == 4) appo.componenti[pos].link_olandese = valore
    if (lang == 5) appo.componenti[pos].link_danese = valore
    if (lang == 6) appo.componenti[pos].link_polacco = valore
    setSezioneSel(appo)
    setAltro({ ...altro, modifiche: true })
  }

  const cambiaBanner = (pos, valore) => {
    if (!sezioneSel) return
    const appo = { ...sezioneSel }
    appo.componenti[pos].id_banner = valore
    setSezioneSel(appo)
    setAltro({ ...altro, modifiche: true })
  }

  const cambiaTitolo = (pos, lang, valore) => {
    if (!sezioneSel) return
    const appo = { ...sezioneSel }
    if (lang == 1) appo.componenti[pos].titolo_italiano = valore
    if (lang == 2) appo.componenti[pos].titolo_inglese = valore
    if (lang == 3) appo.componenti[pos].titolo_tedesco = valore
    if (lang == 4) appo.componenti[pos].titolo_olandese = valore
    if (lang == 5) appo.componenti[pos].titolo_danese = valore
    if (lang == 6) appo.componenti[pos].titolo_polacco = valore
    setSezioneSel(appo)
    setAltro({ ...altro, modifiche: true })
  }

  const cambiaTesto = (pos, lang, valore) => {
    if (!sezioneSel) return
    const appo = { ...sezioneSel }
    if (lang == 1) appo.componenti[pos].valore_italiano = valore
    if (lang == 2) appo.componenti[pos].valore_inglese = valore
    if (lang == 3) appo.componenti[pos].valore_tedesco = valore
    if (lang == 4) appo.componenti[pos].valore_olandese = valore
    if (lang == 5) appo.componenti[pos].valore_danese = valore
    if (lang == 6) appo.componenti[pos].valore_polacco = valore
    //setSezioneSel(appo)
    setAltro({ ...altro, modifiche: true })
  }

  const cambiaTitoloImmagine = (pos, lang, valore) => {
    if (!sezioneSel) return
    const appo = { ...sezioneSel }
    if (lang == 1) appo.componenti[pos].nome_img_italiano = valore
    if (lang == 2) appo.componenti[pos].nome_img_inglese = valore
    if (lang == 3) appo.componenti[pos].nome_img_tedesco = valore
    if (lang == 4) appo.componenti[pos].nome_img_olandese = valore
    if (lang == 5) appo.componenti[pos].nome_img_danese = valore
    if (lang == 6) appo.componenti[pos].nome_img_polacco = valore
    setSezioneSel(appo)
    setAltro({ ...altro, modifiche: true })
  }

  const cambiaImmagine = async (pos, event) => {
    //controllo che sia immagine
    const file = event.target.files[0]
    if (file.type.split('/')[0] != 'image')
      return setAltro({
        ...altro,
        errore: 'Formato del file non valido. Richiesta Immagine.',
      })
    //preparo il body
    let formData = new FormData()
    formData.append('immagine', file, file.name)
    formData.append('nome_italiano', sezioneSel.componenti[pos].nome_img_italiano)
    formData.append('nome_inglese', sezioneSel.componenti[pos].nome_img_inglese)
    formData.append('nome_tedesco', sezioneSel.componenti[pos].nome_img_tedesco)
    formData.append('nome_olandese', sezioneSel.componenti[pos].nome_img_olandese)
    formData.append('nome_danese', sezioneSel.componenti[pos].nome_img_danese)
    formData.append('nome_polacco', sezioneSel.componenti[pos].nome_img_polacco)
    formData.append('componente', sezioneSel.componenti[pos].id)
    //chiama api per creare immagini
    const results = await upload.creaImmagineCms(formData)
    if (!results.ok) return setAltro({ ...altro, errore: results.data })
    //modifico il componente
    const appo = { ...sezioneSel }
    appo.componenti[pos].link_immagine = results.data.link
    appo.componenti[pos].id_immagine = results.data.id
    appo.componenti[pos].nome_img_italiano = results.data.nome_italiano
    appo.componenti[pos].nome_img_inglese = results.data.nome_inglese
    appo.componenti[pos].nome_img_tedesco = results.data.nome_tedesco
    appo.componenti[pos].nome_img_olandese = results.data.nome_olandese
    appo.componenti[pos].nome_img_danese = results.data.nome_danese
    appo.componenti[pos].nome_img_polacco = results.data.nome_polacco
    setSezioneSel(appo)
    console.log(appo.componenti[pos])
    setAltro({ ...altro, modifiche: true })
  }

  const cambiaValoriMetaTitle = (e) => {
    if (!pagina) return
    const key = `meta_title_${LANG_KEYS[lingua - 1]}`
    setPagina((prev) => ({ ...prev, [key]: e.target.value }))
    setAltro((prev) => ({ ...prev, modifiche: true }))
  }

  const cambiaValoriMeta = (e) => {
    if (!pagina) return
    const key = `meta_description_${LANG_KEYS[lingua - 1]}`
    setPagina((prev) => ({ ...prev, [key]: e.target.value }))
    setAltro((prev) => ({ ...prev, modifiche: true }))
  }

  const cambiaOgImage = async (event) => {
    const file = event.target.files?.[0]
    if (!file || file.type.split('/')[0] !== 'image') {
      setAltro((prev) => ({ ...prev, errore: 'Formato del file non valido. Richiesta immagine.' }))
      return
    }
    const formData = new FormData()
    formData.append('immagine', file, file.name)
    formData.append('nome_italiano', pagina?.nome || 'og')
    formData.append('nome_inglese', pagina?.nome || 'og')
    const results = await upload.creaImmagine(formData)
    if (!results.ok) {
      const errMsg = typeof results.data === 'string' ? results.data : results.data?.message || 'Upload fallito.'
      return setAltro((prev) => ({ ...prev, errore: errMsg }))
    }
    const link = results.data?.link ?? (typeof results.data === 'string' ? results.data : null)
    if (!link) return setAltro((prev) => ({ ...prev, errore: 'Risposta upload senza link immagine.' }))
    setPagina((prev) => ({ ...prev, og_image: link }))
    setAltro((prev) => ({ ...prev, modifiche: true, errore: null }))
    event.target.value = ''
  }

  const cliccaElemento = (sezione) => {
    const idPagina = sezione.pagina
    const appo = pagine.filter((p) => p.id == idPagina)[0]
    if (appo) {
      setPagina(appo)
      setSezioneSel(sezione)
    }
  }

  const premiAnnulla = () => {
    if (!sezioneSel || !pagina) return
    if (sezioneSel.id === 'seo') {
      const fresh = pagine.find((p) => p.id === pagina.id)
      if (fresh) setPagina(fresh)
      setAltro({ ...altro, modifiche: false })
      return
    }
    if (sezioneSel.id === 'faq') {
      const fresh = pagine.find((p) => p.id === pagina.id)
      if (fresh) {
        setPagina(fresh)
        setFaqList(normalizeFaqList(fresh.faq))
      }
      setAltro({ ...altro, modifiche: false })
      return
    }
    //pulisco sezioni
    const vett = JSON.parse(JSON.stringify(elementi))
    const lista = vett.filter((el) => el.pagina == pagina.id)
    setSezioni(lista)
    //pulisco sezione sel
    const appo = lista.filter((s) => s.id == sezioneSel.id)[0]
    if (appo) setSezioneSel(appo)
    else setSezioneSel(null)
    //annullo modifiche
    setAltro({ ...altro, modifiche: false })
  }

  const confermaModifiche = async () => {
    if (altro.loading) return
    if (sezioneSel?.id === 'seo') {
      if (!pagina?.id) return setAltro((prev) => ({ ...prev, errore: 'Pagina non selezionata.' }))
      setAltro({ ...altro, loading: true, errore: null })
      const payload = {
        id: pagina.id,
        nome: pagina.nome,
        link: pagina.link ?? '',
        meta_title_italiano: pagina.meta_title_italiano ?? null,
        meta_title_inglese: pagina.meta_title_inglese ?? null,
        meta_title_tedesco: pagina.meta_title_tedesco ?? null,
        meta_title_olandese: pagina.meta_title_olandese ?? null,
        meta_title_danese: pagina.meta_title_danese ?? null,
        meta_title_polacco: pagina.meta_title_polacco ?? null,
        meta_description_italiano: pagina.meta_description_italiano ?? null,
        meta_description_inglese: pagina.meta_description_inglese ?? null,
        meta_description_tedesco: pagina.meta_description_tedesco ?? null,
        meta_description_olandese: pagina.meta_description_olandese ?? null,
        meta_description_danese: pagina.meta_description_danese ?? null,
        meta_description_polacco: pagina.meta_description_polacco ?? null,
        og_image: pagina.og_image ?? '',
      }
      const results = await cmsApi.modificaPagina(payload)
      if (!results.ok) {
        const errMsg = typeof results.data === 'string' ? results.data : results.data?.message || results.data
        return setAltro({ ...altro, loading: false, errore: errMsg })
      }
      const updated = results.data
      modificaPagina(updated)
      setPagina(updated)
      setAltro({ ...altro, loading: false, modifiche: false, openAlert: 2 })
      return
    }
    if (sezioneSel?.id === 'faq') {
      if (!pagina?.id) return setAltro((prev) => ({ ...prev, errore: 'Pagina non selezionata.' }))
      const faqPayload = buildCmsPaginaFaqPayload(pagina.faq_abilitato, faqList)
      setAltro({ ...altro, loading: true, errore: null })
      const results = await cmsApi.modificaFaq(pagina.id, faqPayload)
      if (!results.ok) {
        const errMsg = typeof results.data === 'string' ? results.data : results.data?.message || results.data
        return setAltro((prev) => ({ ...prev, loading: false, errore: errMsg }))
      }
      const updated = results.data
      modificaPagina(updated)
      setPagina(updated)
      setFaqList(normalizeFaqList(updated.faq))
      setAltro({ ...altro, loading: false, modifiche: false, openAlert: 2 })
      return
    }
    setAltro({ ...altro, loading: true, errore: null })
    const results = await cmsApi.modificaComponenti(sezioneSel.id, sezioneSel.componenti)
    if (!results.ok) return setAltro({ ...altro, loading: false, errore: results.data })
    setTimeout(() => {
      modificaElemento(results.data)
      aggSezioni()
      setSezioneSel(results.data)
      setAltro({
        ...altro,
        loading: false,
        errore: null,
        modifiche: false,
        openAlert: 5,
      })
    }, 500)
  }

  const confermaModal = () => {
    if (modifiche.tipo == 1) {
      //cambio pagina
      setPagina(modifiche.appoPagina)
      setSezioneSel(null)
    } else {
      //cambio sezione
      setSezioneSel(null)
      aggSezioni()
    }
    setAltro({ ...altro, modifiche: false, visible: false })
  }

  const vediPagina = () => {
    const link = config.sitoUrl + pagina.link
    window.open(link, '_blank', 'noopener,noreferrer')
  }

  const chiudiAlert = (event, reason) => {
    if (reason === 'clickaway') return
    setAltro({ ...altro, loading: false, errore: null, openAlert: 0 })
  }

  const langKeyForFaq = LANG_KEYS[lingua - 1] || LANG_KEYS[0]
  const cmsFaqContext = useMemo(
    () => ({
      pageTitle: buildCmsFaqPageTitle(pagina, langKeyForFaq),
      pageBody: buildCmsFaqPageBody(sezioni, langKeyForFaq, pagina),
    }),
    [pagina, sezioni, lingua]
  )

  return (
    <React.Fragment>
      <div className="admin-corpo-modifica">
        <div className="admin-corpo-modifica-sx">
          <BarraPagineCms
            pagine={pagine}
            paginaSel={pagina}
            isSuper={utente?.is_super}
            loading={altro.loading}
            scegliPagina={scegliPagina}
            goToNuova={() => navigate('/cms/gestisci-pagina', { state: { fromCms: true } })}
            premiAnnulla={premiAnnulla}
            confermaModifiche={confermaModifiche}
          ></BarraPagineCms>
        </div>

        <div className="admin-corpo-modifica-dx">
          <HeaderCms
            pagina={pagina}
            sezioni={sezioni}
            sezioneSel={sezioneSel}
            isSuper={utente?.is_super}
            isModifiche={Boolean(altro.modifiche && sezioneSel)}
            scegliSezione={scegliSezione}
            goToModifica={() =>
              navigate('/cms/gestisci-pagina', {
                state: { fromCms: true, pagina },
              })
            }
            goToNuovaSezione={() =>
              navigate('/cms/gestisci-sezione', {
                state: { idPagina: pagina.id },
              })
            }
            goToModificaSezione={() =>
              navigate('/cms/gestisci-sezione', {
                state: { idPagina: pagina.id, sezione: sezioneSel },
              })
            }
            annullaSezione={annullaSezione}
            vediPagina={() => (pagina ? vediPagina() : null)}
            loading={altro.loading}
            premiAnnulla={() => premiAnnulla()}
            confermaModifiche={async () => await confermaModifiche()}
            cambiaPagina={() => setPagina(null)}
          ></HeaderCms>

          {!sezioneSel && !pagina && (
            <RicercaCms elementi={elementi} pagina={pagina} cliccaElemento={cliccaElemento}></RicercaCms>
          )}

          {sezioneSel?.id === 'seo' && pagina && (
            <>
              <MetaScheda
                lingua={lingua}
                cambiaLingua={setLingua}
                valoriMetaTitle={{
                  italiano: pagina.meta_title_italiano ?? '',
                  inglese: pagina.meta_title_inglese ?? '',
                  tedesco: pagina.meta_title_tedesco ?? '',
                  olandese: pagina.meta_title_olandese ?? '',
                  danese: pagina.meta_title_danese ?? '',
                  polacco: pagina.meta_title_polacco ?? '',
                }}
                cambiaValoriMetaTitle={cambiaValoriMetaTitle}
                valoriMeta={{
                  italiano: pagina.meta_description_italiano ?? '',
                  inglese: pagina.meta_description_inglese ?? '',
                  tedesco: pagina.meta_description_tedesco ?? '',
                  olandese: pagina.meta_description_olandese ?? '',
                  danese: pagina.meta_description_danese ?? '',
                  polacco: pagina.meta_description_polacco ?? '',
                }}
                cambiaValoriMeta={cambiaValoriMeta}
                isModifica
                favouriteImageUrl={pagina.og_image || getFirstPageImage(sezioni)}
                resourceData={pagina.nome || null}
              />
              <div className="admin-container-lungo">
                <h3 className="admin-titoletto">Immagine social (OG)</h3>
                <p style={{ fontSize: 14, color: '#666', marginBottom: 8 }}>
                  {pagina.og_image
                    ? 'Immagine personalizzata per condivisione social.'
                    : "Default: prima immagine della pagina (se presente). Carica un'immagine per sovrascriverla."}
                </p>
                <Stack paddingTop={1}>
                  <input
                    id="og-image-upload-file"
                    type="file"
                    accept="image/*"
                    onChange={cambiaOgImage}
                    style={{ display: 'none' }}
                  />
                  <label className="admin-linea-bottone-immagine" htmlFor="og-image-upload-file">
                    <Button variant="contained" color="primary" component="span" startIcon={<FileUploadIcon />}>
                      Upload
                    </Button>
                  </label>
                </Stack>
                {pagina.og_image && (
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ marginTop: 2 }}>
                    <img
                      src={pagina.og_image}
                      alt="OG"
                      loading="lazy"
                      style={{ maxWidth: 200, maxHeight: 120, objectFit: 'cover', borderRadius: 4 }}
                    />
                    <IconButton
                      aria-label="rimuovi immagine social"
                      onClick={() => setConfirmDeleteOgImage((prev) => ({ ...prev, open: true }))}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                )}
              </div>
            </>
          )}

          {sezioneSel?.id === 'faq' && pagina && (
            <FAQScheda
              lingua={lingua}
              cambiaLingua={setLingua}
              faqAbilitato={Boolean(pagina.faq_abilitato)}
              cambiaFaqAbilitato={cambiaFaqAbilitato}
              faqList={faqList}
              cambiaFaqItem={cambiaFaqItem}
              aggiungiFaqItem={aggiungiFaqItem}
              rimuoviFaqItem={rimuoviFaqItem}
                trimFaqListToLength={trimFaqListToLength}
              cmsFaqContext={cmsFaqContext}
              isModifica={true}
            />
          )}

          {sezioneSel && sezioneSel.id !== 'seo' && sezioneSel.id !== 'faq' && (
            <GestisciComponenti
              componenti={sezioneSel.componenti}
              cambiaLink={cambiaLink}
              cambiaTitolo={cambiaTitolo}
              cambiaImmagine={cambiaImmagine}
              cambiaTesto={cambiaTesto}
              cambiaTitoloImmagine={cambiaTitoloImmagine}
              cambiaBanner={cambiaBanner}
            ></GestisciComponenti>
          )}

          {sezioneSel && altro.modifiche && (
            <BottoniScheda
              loading={altro.loading}
              conferma={async () => await confermaModifiche()}
              annulla={() => premiAnnulla()}
            ></BottoniScheda>
          )}
        </div>
      </div>

      <ModalAnnullaCms
        visible={altro.visible}
        loading={altro.loading}
        annulla={() => setAltro({ ...altro, visible: false })}
        conferma={confermaModal}
      ></ModalAnnullaCms>

      <Dialog
        open={confirmDeleteOgImage.open}
        onClose={() => !confirmDeleteOgImage.loading && setConfirmDeleteOgImage({ open: false, loading: false })}
        aria-labelledby="dialog-delete-og-image"
      >
        <DialogTitle id="dialog-delete-og-image">Eliminare immagine social (OG)?</DialogTitle>
        <DialogContent>
          <DialogContentText>Sei sicuro? Una volta eliminata non potrai riselezionarla.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDeleteOgImage({ open: false, loading: false })}
            disabled={confirmDeleteOgImage.loading}
          >
            Annulla
          </Button>
          <LoadingButton
            loading={confirmDeleteOgImage.loading}
            color="error"
            variant="contained"
            onClick={async () => {
              if (!pagina?.og_image || !pagina?.id) {
                setConfirmDeleteOgImage({ open: false, loading: false })
                return
              }
              const link = pagina.og_image
              setConfirmDeleteOgImage((prev) => ({ ...prev, loading: true }))
              try {
                await upload.eliminaImmagineByLink(link)
                setPagina((prev) => ({ ...prev, og_image: null }))
                // Persist immediately so CONFERMA/ANNULLA are not shown (deletion cannot be cancelled)
                const payload = {
                  id: pagina.id,
                  nome: pagina.nome,
                  link: pagina.link ?? '',
                  meta_title_italiano: pagina.meta_title_italiano ?? null,
                  meta_title_inglese: pagina.meta_title_inglese ?? null,
                  meta_title_tedesco: pagina.meta_title_tedesco ?? null,
                  meta_title_olandese: pagina.meta_title_olandese ?? null,
                  meta_title_danese: pagina.meta_title_danese ?? null,
                  meta_title_polacco: pagina.meta_title_polacco ?? null,
                  meta_description_italiano: pagina.meta_description_italiano ?? null,
                  meta_description_inglese: pagina.meta_description_inglese ?? null,
                  meta_description_tedesco: pagina.meta_description_tedesco ?? null,
                  meta_description_olandese: pagina.meta_description_olandese ?? null,
                  meta_description_danese: pagina.meta_description_danese ?? null,
                  meta_description_polacco: pagina.meta_description_polacco ?? null,
                  og_image: '',
                }
                const results = await cmsApi.modificaPagina(payload)
                if (!results.ok) {
                  const errMsg = typeof results.data === 'string' ? results.data : results.data?.message || results.data
                  setAltro((prev) => ({ ...prev, errore: errMsg ?? 'Errore aggiornamento pagina.' }))
                  setConfirmDeleteOgImage({ open: false, loading: false })
                  return
                }
                const updated = results.data
                modificaPagina(updated)
                setPagina(updated)
                setAltro((prev) => ({ ...prev, modifiche: false, openAlert: 2 }))
                setConfirmDeleteOgImage({ open: false, loading: false })
              } catch (e) {
                setAltro((prev) => ({
                  ...prev,
                  errore: e?.response?.data || 'Errore eliminazione immagine.',
                }))
                setConfirmDeleteOgImage({ open: false, loading: false })
              }
            }}
          >
            Elimina
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={altro.errore?.length > 0}
        onClose={chiudiAlert}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        style={{ top: 70 }}
      >
        <Alert onClose={chiudiAlert} variant="filled" severity="error" sx={{ width: '100%' }}>
          {altro.errore}
        </Alert>
      </Snackbar>
      <Snackbar
        open={altro.openAlert == 1}
        onClose={chiudiAlert}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        style={{ top: 70 }}
      >
        <Alert onClose={chiudiAlert} variant="filled" severity="success" sx={{ width: '100%' }}>
          Pagina creata con successo!
        </Alert>
      </Snackbar>
      <Snackbar
        open={altro.openAlert == 2}
        onClose={chiudiAlert}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        style={{ top: 70 }}
      >
        <Alert onClose={chiudiAlert} variant="filled" severity="success" sx={{ width: '100%' }}>
          Pagina modificata con successo!
        </Alert>
      </Snackbar>
      <Snackbar
        open={altro.openAlert == 3}
        onClose={chiudiAlert}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        style={{ top: 70 }}
      >
        <Alert onClose={chiudiAlert} variant="filled" severity="success" sx={{ width: '100%' }}>
          Sezione creata con successo!
        </Alert>
      </Snackbar>
      <Snackbar
        open={altro.openAlert == 4}
        onClose={chiudiAlert}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        style={{ top: 70 }}
      >
        <Alert onClose={chiudiAlert} variant="filled" severity="success" sx={{ width: '100%' }}>
          Sezione modificata con successo!
        </Alert>
      </Snackbar>
      <Snackbar
        open={altro.openAlert == 5}
        onClose={chiudiAlert}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        style={{ top: 70 }}
      >
        <Alert onClose={chiudiAlert} variant="filled" severity="success" sx={{ width: '100%' }}>
          Sezione modificata con successo!
        </Alert>
      </Snackbar>
      <Snackbar
        open={altro.openAlert == 6}
        onClose={chiudiAlert}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        style={{ top: 70 }}
      >
        <Alert onClose={chiudiAlert} variant="filled" severity="success" sx={{ width: '100%' }}>
          Pagina eliminata con successo!
        </Alert>
      </Snackbar>
      <Snackbar
        open={altro.openAlert == 7}
        onClose={chiudiAlert}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        style={{ top: 70 }}
      >
        <Alert onClose={chiudiAlert} variant="filled" severity="success" sx={{ width: '100%' }}>
          Sezione eliminata con successo!
        </Alert>
      </Snackbar>
    </React.Fragment>
  )
}

export default HomeCms
